// Kundenliste — 223 Zeilen, also ist Filtern und Suchen wichtiger als Blättern.
//
// Jede Zeile zeigt genau das, was der Bauer zum Wiedererkennen braucht:
// Name (oder Adresse, wenn namenlos), Straße, Lieferhinweis, Wunschzeit.

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomerList, lieferTage, type CustomerListItem } from "@/lib/crm";
import { LogoLoader } from "@/components/LogoLoader";
import { Button, EmptyState, Input, cn } from "@/design-system/ui";
import { useSidebar } from "@/modules/home/SidebarContext";
import { useAuth } from "@/stores/authStore";
import { useKundenpflege, useKundenpflegeSetzen } from "@/lib/kundenpflege";

const TAGE: { wert: number; kurz: string }[] = [
  { wert: 1, kurz: "Mo" },
  { wert: 2, kurz: "Di" },
  { wert: 3, kurz: "Mi" },
  { wert: 4, kurz: "Do" },
  { wert: 5, kurz: "Fr" },
  // Samstag fehlte hier, obwohl er überall sonst gilt: Die Liefertage in den
  // Einstellungen gehen von Mo bis Sa, und die Zeile am Kunden zeigt „Sa“ auch
  // an. Nur filtern liess sich danach nicht — ein Samstagskunde war über die
  // Leiste nicht auffindbar.
  { wert: 6, kurz: "Sa" },
];

export default function CustomerListPage() {
  const [search, setSearch] = useState("");
  // Mehrfachauswahl: leere Menge = alle Kunden.
  const [tage, setTage] = useState<Set<number>>(new Set());
  // Der Tag geht MIT in die Abfrage. Filterte die Seite erst nach dem Laden,
  // sähe sie nur, was zufällig in den ersten Zeilen stand — siehe useCustomerList.
  const { data: customers = [], isLoading } = useCustomerList(search, [...tage]);
  const navigate = useNavigate();
  const { oeffnen } = useSidebar();
  const istAdmin = useAuth((s) => s.role) === "admin";

  const alle = tage.size === 0;

  const toggleTag = (d: number) =>
    setTage((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return customers.filter((c) => {
      // Der Wochentag ist schon in der Datenbank gefiltert (useCustomerList).
      if (s.length < 2) return true;
      // Der Bauer sucht oft nach der Straße — die Liste ist klein genug,
      // dass sich dafür kein zusätzlicher Roundtrip lohnt.
      return (
        c.display_name.toLowerCase().includes(s) ||
        (c.customer_number ?? "").toLowerCase().includes(s) ||
        c.addresses?.some((a) => a.street.toLowerCase().includes(s))
      );
    });
  }, [customers, search, tage, alle]);

  const gewaehlteNamen = TAGE.filter((t) => tage.has(t.wert))
    .map((t) => t.kurz)
    .join(", ");

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col">
      <header className="sticky top-0 z-10 border-b border-ink-700/70 bg-ink-950/95 px-4 py-3 backdrop-blur">
        {/* Drei Striche = Verwaltung, also nur für Admins. Für alle anderen läge
            dahinter ein leeres Menü. Zurück zum Startbildschirm führt "Start"
            ganz links in der unteren Leiste — der Weg geht niemandem verloren.
            —
            44 px wie in ModulHeader: Derselbe Knopf mit derselben Funktion war
            hier 36 px groß und dort 44 — beim Seitenwechsel sprang er. */}
        {istAdmin && (
          <button
            onClick={oeffnen}
            aria-label="Menü öffnen"
            data-testid="menu-button"
            className="-ml-2 mb-1 flex h-11 w-11 items-center justify-center rounded-lg text-ink-400 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
              <g stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                <path d="M4 7h16" />
                <path d="M4 12h16" />
                <path d="M4 17h16" />
              </g>
            </svg>
          </button>
        )}

        {/* Suche + Neuanlage — eine Höhe, eine Textgröße */}
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, Straße oder Kundennummer …"
            className="flex-1"
            autoComplete="off"
          />
          <Button variant="primary" onClick={() => navigate("/crm/neu")} className="shrink-0">
            + Kunde
          </Button>
        </div>

        {/* Liefertag-Filter, MEHRFACHauswahl. "Alle" ist kein eigener Zustand,
            sondern schlicht "nichts gewählt" — so kann man mit einem Tipp
            zurücksetzen, ohne jeden Tag einzeln abwählen zu müssen. */}
        <div className="-mx-4 mt-3 flex gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <FilterChip aktiv={alle} onClick={() => setTage(new Set())}>
            Alle
          </FilterChip>

          {TAGE.map((t) => (
            <FilterChip
              key={t.wert}
              aktiv={tage.has(t.wert)}
              onClick={() => toggleTag(t.wert)}
            >
              {t.kurz}
            </FilterChip>
          ))}
        </div>

        <p className="mt-2 text-xs tabular-nums text-ink-500">
          {isLoading
            ? "lädt …"
            : alle
              ? `${filtered.length} Kunden`
              : `${filtered.length} Kunden · ${gewaehlteNamen}`}
        </p>
      </header>

      <KundenpflegeStreifen />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <LogoLoader size="md" label="Kunden werden geladen" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="Kein Kunde gefunden"
            hint={
              search
                ? `Nichts passt zu „${search}"${alle ? "" : ` (${gewaehlteNamen})`}.`
                : `An ${gewaehlteNamen} wird niemand beliefert.`
            }
            action={
              alle ? (
                <Button variant="primary" onClick={() => navigate("/crm/neu")}>
                  Kunde anlegen
                </Button>
              ) : (
                <Button onClick={() => setTage(new Set())}>Filter zurücksetzen</Button>
              )
            }
          />
        ) : (
          <ul className="divide-y divide-ink-800">
            {filtered.map((c) => (
              <KundenZeile key={c.id} c={c} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  aktiv,
  onClick,
  children,
}: {
  aktiv: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={aktiv}
      className={cn(
        "flex h-9 shrink-0 items-center justify-center rounded-full border px-4 text-sm font-medium",
        "transition-colors duration-fast",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
        aktiv
          ? "border-gruen-500/50 bg-gruen-500/15 text-gruen-400"
          : "border-ink-700 bg-ink-800/60 text-ink-300 hover:border-ink-600 hover:text-ink-100"
      )}
    >
      {children}
    </button>
  );
}

const TAG_KURZ = ["Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function KundenZeile({ c }: { c: CustomerListItem }) {
  const addr = c.addresses?.[0];
  const ohneGeo = addr && !addr.geo_lat;
  const tage = lieferTage(c);

  return (
    <li>
      <Link
        to={`/crm/${c.id}`}
        className="flex items-center gap-3 px-4 py-3 transition-colors duration-fast hover:bg-ink-900"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-[15px] font-medium text-ink-100">{c.display_name}</span>
            {c.preferred_time && (
              <span className="shrink-0 rounded bg-gruen-500/15 px-1.5 py-0.5 text-xs font-medium tabular-nums text-gruen-400">
                {c.preferred_time.slice(0, 5)}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-sm text-ink-400">
            <span className="truncate">{addr?.street ?? "keine Adresse"}</span>
            {ohneGeo && (
              <span className="shrink-0 text-warn-fg" title="Ohne Koordinaten — fällt aus der Route">
                ⚠
              </span>
            )}
          </div>
          {c.notes && <div className="mt-0.5 truncate text-sm text-ink-500">{c.notes}</div>}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="font-mono text-xs tabular-nums text-ink-500">{c.customer_number}</span>
          {tage.length > 0 && (
            <span className="text-xs font-medium text-ink-400">
              {tage.map((d) => TAG_KURZ[d - 1]).join(" ")}
            </span>
          )}
        </div>
      </Link>
    </li>
  );
}

// ─────────────────────────────────────────────── Kundenpflege-Fortschritt

/**
 * Zeigt, wie weit der Kundenstamm durchgesehen ist — und nur dann, wenn der
 * Modus läuft. Danach verschwindet der Streifen wieder.
 *
 * Der Fortschritt gehört hierher und nicht in einen Verwaltungswinkel: Wer die
 * Kundenliste offen hat, denkt gerade über Kundendaten nach. Und der Bauer sieht
 * so nebenbei, ob seine Leute wirklich abhaken.
 */
function KundenpflegeStreifen() {
  const { data: stand } = useKundenpflege();
  const setzen = useKundenpflegeSetzen();
  const istAdmin = useAuth((s) => s.role) === "admin";

  if (!stand?.aktiv) return null;

  const gesamt = stand.offen + stand.bestaetigt;
  const anteil = gesamt ? Math.round((stand.bestaetigt / gesamt) * 100) : 0;
  const bis = stand.bis
    ? new Date(stand.bis).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })
    : "";

  return (
    <div className="shrink-0 border-b border-ink-700/70 bg-gruen-500/5 px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-gruen-400">Kundenpflege läuft</span>
        <span className="text-xs tabular-nums text-ink-400">bis {bis}</span>
      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink-800">
        <div
          className="h-full rounded-full bg-gruen-500 transition-[width] duration-slow"
          style={{ width: `${anteil}%` }}
        />
      </div>

      <div className="mt-2 flex items-baseline justify-between gap-3">
        <span className="text-sm tabular-nums text-ink-300">
          {stand.bestaetigt} von {gesamt} bestätigt
          {stand.offen > 0 && <span className="text-ink-500"> · {stand.offen} offen</span>}
        </span>
        {istAdmin && (
          <button
            onClick={() => setzen.mutate(null)}
            disabled={setzen.isPending}
            className="shrink-0 text-xs font-medium text-ink-400 underline underline-offset-2 hover:text-ink-100"
          >
            Beenden
          </button>
        )}
      </div>
    </div>
  );
}
